/* BW-215 full build: CSS-cuboid rotatable book, the DEFAULT view for a
 * qualifying book (front_cover plus at least one of spine/back_cover -
 * product-gallery.liquid's show_cuboid gate). A missing face is rendered
 * server-side as a derived flat face (product-cuboid.liquid); this file
 * only drives rotation, the flat/3D toggle, and the face-strip/keyboard/
 * drag destinations - the derived-face markup itself needs no JS at all.
 *
 * Loaded once as a script[src] beside product-gallery.js; the BW-213 modal's
 * rehydrateScripts() re-executes a script exactly once per resolved URL for
 * the life of the page, so every interaction here is a document-level
 * delegated listener bound once - never a per-element init call - the same
 * pattern product-gallery.js and pdp-modal.js already use. Per-book rotation
 * state lives in a WeakMap keyed by the .pdp-cuboid element, so the real PDP
 * and a modal-mounted copy of a different book never share angles.
 *
 * Also exposes window.BookwormsCuboid.flatForMorph/.restore for pdp-modal.js's
 * BW-214 morph - see that file's top comment for why a cuboid-default book
 * needs its .pdp-cover-frame flipped visible before the morph names it.
 */
(function () {
  "use strict";

  if (window.__bwCuboid) return;
  window.__bwCuboid = 1;

  var PRESENTATION_YAW = 24;
  var PRESENTATION_PITCH = -8;
  // The angle rendered at first paint (see product-cuboid.liquid) - the
  // one-off settle (below) sweeps from here up to the presentation angle,
  // so there is no flash between a CSS default and a JS correction.
  var PRE_SETTLE_YAW = 6;
  var SETTLE_MS = 900;
  // Raised from 18 (concept demo) to 90 at Adam's call (BW-215 comment
  // 11528/11530) - past 90 the book would be upside down and yaw would need
  // to reverse to keep turning sensibly, so clamping at the boundary rather
  // than modelling that flip.
  var PITCH_LIMIT = 90;
  var DRAG_LOCK_PX = 6;

  var STATE = new WeakMap();
  var activeDrag = null;
  var observedCuboids = new WeakSet();
  var sharedObserver = null;
  var settleFired = false;

  if (/(?:^|[?&])bwcuboid=fill(?:&|$)/.test(location.search)) {
    document.documentElement.classList.add("bw-cuboid-fill");
  }

  function reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function getState(cuboidEl) {
    var st = STATE.get(cuboidEl);
    if (!st) {
      st = {
        book: cuboidEl.querySelector(".pdp-cuboid-book"),
        yaw: PRE_SETTLE_YAW,
        pitch: PRESENTATION_PITCH,
        rafScheduled: false
      };
      STATE.set(cuboidEl, st);
    }
    return st;
  }

  function applyAngle(st) {
    st.book.style.setProperty("--bk-yaw", st.yaw + "deg");
    st.book.style.setProperty("--bk-pitch", st.pitch + "deg");
  }

  function setAngle(cuboidEl, yaw, pitch) {
    var st = getState(cuboidEl);
    st.yaw = yaw;
    st.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
    applyAngle(st);
  }

  function scheduleApply(st) {
    if (st.rafScheduled) return;
    st.rafScheduled = true;
    requestAnimationFrame(function () {
      st.rafScheduled = false;
      applyAngle(st);
    });
  }

  // The one-off "pull toward camera" settle, first intersection only, once
  // for the whole page - a book opened later (a second qualifying book in
  // the BW-213 modal) just appears at rest, no second sweep.
  function runSettle(cuboidEl) {
    var st = getState(cuboidEl);
    st.book.style.transitionDuration = SETTLE_MS + "ms";
    st.yaw = PRESENTATION_YAW;
    applyAngle(st);
    st.book.addEventListener("transitionend", function handler(e) {
      if (e.propertyName !== "transform") return;
      st.book.removeEventListener("transitionend", handler);
      st.book.style.transitionDuration = "";
    });
  }

  function getSharedObserver() {
    if (!sharedObserver) {
      sharedObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          sharedObserver.unobserve(entry.target);
          var cuboidEl = entry.target.closest(".pdp-cuboid");
          if (settleFired) {
            // BW-442: a hidden copy's cuboid, revealed after the settle was spent.
            if (cuboidEl) setAngle(cuboidEl, PRESENTATION_YAW, PRESENTATION_PITCH);
            return;
          }
          settleFired = true;
          if (cuboidEl) runSettle(cuboidEl);
        });
      }, { threshold: 0.2 });
    }
    return sharedObserver;
  }

  // Arms the one-off settle for a cuboid the first time it's seen - a fresh
  // page load's own cuboid, or one just mounted into the BW-213 modal.
  // Reduced motion, or the settle already spent elsewhere on the page,
  // skips straight to the presentation angle with no sweep.
  function ensureObserved(cuboidEl) {
    if (observedCuboids.has(cuboidEl)) return;
    observedCuboids.add(cuboidEl);
    if (settleFired || reducedMotion()) {
      setAngle(cuboidEl, PRESENTATION_YAW, PRESENTATION_PITCH);
      return;
    }
    var stage = cuboidEl.querySelector(".pdp-cuboid-stage");
    if (stage) getSharedObserver().observe(stage);
  }

  function scanForCuboids(root) {
    if (!root.querySelectorAll) return;
    var nodes = root.querySelectorAll(".pdp-cuboid");
    for (var i = 0; i < nodes.length; i++) ensureObserved(nodes[i]);
  }

  scanForCuboids(document);
  // Catches a cuboid mounted later (the BW-213 modal's innerHTML replace
  // never fires DOMContentLoaded again) without re-running this script -
  // rehydrateScripts() only re-executes a script[src] once per URL.
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(".pdp-cuboid")) ensureObserved(node);
        else scanForCuboids(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  function toggleCuboid(table, btn) {
    var cover = table.querySelector(".pdp-cover-frame");
    var cuboid = table.querySelector(".pdp-cuboid");
    if (!cover || !cuboid) return;
    var goingFlat = !cuboid.hidden; // cuboid showing now -> this click shows flat instead
    cuboid.hidden = goingFlat;
    cover.hidden = !goingFlat;
    btn.setAttribute("aria-pressed", goingFlat ? "true" : "false");
    // Label names the action available next, not the view already showing -
    // both strings come from product-gallery.liquid's data attributes.
    btn.textContent = goingFlat ? btn.dataset.labelCuboid : btn.dataset.labelFlat;
    var note = table.querySelector(".pdp-view-note");
    if (note) note.textContent = goingFlat ? note.dataset.noteFlat : note.dataset.noteCuboid;
    // BW-215: the drag hint sits above the main image and only makes sense
    // while the cuboid is showing.
    var hint = table.querySelector(".pdp-cuboid-hint");
    if (hint) hint.hidden = goingFlat;
    if (!goingFlat) {
      ensureObserved(cuboid);
      var stage = cuboid.querySelector(".pdp-cuboid-stage");
      if (stage) stage.focus({ preventScroll: true });
    }
  }

  document.addEventListener("click", function (e) {
    var toggle = e.target.closest ? e.target.closest(".pdp-cuboid-toggle") : null;
    if (toggle) {
      var table = toggle.closest(".pdp-gallery");
      if (table) toggleCuboid(table, toggle);
      return;
    }

    // Face-strip destination buttons (Front cover / Spine / Back cover /
    // Fore edge) - see product-cuboid.liquid for the yaw values, corrected
    // from the concept demo's backwards spine sign. Only the Front cover
    // destination also resets pitch to a clean straight-on view; the others
    // leave whatever up/down angle the shopper already had.
    var dest = e.target.closest ? e.target.closest(".pdp-cuboid-dest") : null;
    if (dest) {
      var cuboid = dest.closest(".pdp-cuboid");
      if (!cuboid) return;
      var st = getState(cuboid);
      var yaw = parseFloat(dest.getAttribute("data-yaw"));
      var pitch = dest.hasAttribute("data-reset-pitch") ? 0 : st.pitch;
      setAngle(cuboid, yaw, pitch);
      return;
    }

    // A face thumb (front cover/spine/back cover) click while the cuboid is
    // showing turns it to the matching face instead of (as well as)
    // swapping the flat cover-frame image - product-gallery.js's own
    // listener still runs unmodified. A thumb that isn't one of the
    // cuboid's three faces (an extras photo - copyright/page/measure/other -
    // or the duplicate front-cover thumb shown when there's no scatter row)
    // has nothing to turn to, so it switches to the flat gallery instead,
    // exactly as pressing "Flat photographs" would; product-gallery.js's
    // listener then shows the tapped image large.
    var thumb = e.target.closest ? e.target.closest(".pdp-thumb") : null;
    if (!thumb) return;
    var thumbTable = thumb.closest(".pdp-gallery");
    var thumbCuboid = thumbTable && thumbTable.querySelector(".pdp-cuboid");
    if (!thumbCuboid || thumbCuboid.hidden) return;
    var full = thumb.getAttribute("data-full");
    if (full === thumbCuboid.dataset.frontSrc) {
      setAngle(thumbCuboid, 0, 0);
    } else if (full === thumbCuboid.dataset.spineSrc) {
      setAngle(thumbCuboid, 90, 0);
    } else if (full === thumbCuboid.dataset.backSrc) {
      setAngle(thumbCuboid, 180, 0);
    } else {
      var toggle = thumbTable.querySelector(".pdp-cuboid-toggle");
      if (toggle) toggleCuboid(thumbTable, toggle);
    }
  });

  document.addEventListener("keydown", function (e) {
    var stage = e.target.closest ? e.target.closest(".pdp-cuboid-stage") : null;
    if (!stage) return;
    var cuboid = stage.closest(".pdp-cuboid");
    if (!cuboid) return;
    var st = getState(cuboid);
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setAngle(cuboid, st.yaw - 15, st.pitch);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setAngle(cuboid, st.yaw + 15, st.pitch);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAngle(cuboid, st.yaw, st.pitch + 15);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setAngle(cuboid, st.yaw, st.pitch - 15);
    } else if (e.key === "Home") {
      e.preventDefault();
      setAngle(cuboid, PRESENTATION_YAW, PRESENTATION_PITCH);
    }
  });

  document.addEventListener("pointerdown", function (e) {
    var stage = e.target.closest ? e.target.closest(".pdp-cuboid-stage") : null;
    if (!stage) return;
    var cuboid = stage.closest(".pdp-cuboid");
    if (!cuboid) return;
    var st = getState(cuboid);
    activeDrag = {
      state: st,
      startX: e.clientX,
      startY: e.clientY,
      startYaw: st.yaw,
      startPitch: st.pitch,
      decided: false,
      locked: false
    };
    try { stage.setPointerCapture(e.pointerId); } catch (err) { /* unsupported, drag still works */ }
    st.book.classList.add("is-dragging");
  });

  document.addEventListener("pointermove", function (e) {
    if (!activeDrag) return;
    var dx = e.clientX - activeDrag.startX;
    var dy = e.clientY - activeDrag.startY;
    if (!activeDrag.decided && (Math.abs(dx) > DRAG_LOCK_PX || Math.abs(dy) > DRAG_LOCK_PX)) {
      activeDrag.decided = true;
      activeDrag.locked = Math.abs(dx) > Math.abs(dy);
    }
    // Only a drag that has locked to rotation (horizontal-dominant) turns
    // the book - an undecided or vertical-locked drag changes nothing, so a
    // vertical touch gesture always ends up as a plain page scroll
    // (touch-action: pan-y) with no mid-gesture tilt first.
    if (!activeDrag.locked) return;
    e.preventDefault();
    var st = activeDrag.state;
    st.yaw = activeDrag.startYaw + dx * 0.55;
    // BW-215: the face under the pointer must follow the finger - dragging up
    // (dy negative) tips the top of the book toward the viewer (pitch grows),
    // dragging down tips it away, so the delta is subtracted, not added.
    var pitch = activeDrag.startPitch - dy * 0.15;
    st.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
    scheduleApply(st);
  });

  function endDrag() {
    if (!activeDrag) return;
    activeDrag.state.book.classList.remove("is-dragging");
    activeDrag = null;
  }
  document.addEventListener("pointerup", endDrag);
  document.addEventListener("pointercancel", endDrag);

  // BW-214's morph names .pdp-cover-frame, which stays hidden by default on
  // a cuboid-default book - without this, the morph would name a
  // non-rendered element and the cover would fly off the shelf tile and
  // vanish instead of landing (the blocking finding in BW-215 comment
  // 11530). Both are no-ops for a book with no cuboid at all.
  window.BookwormsCuboid = {
    flatForMorph: function (root) {
      root = root.querySelector(".pdp-gallery:not([hidden])") || root;
      var cuboid = root.querySelector(".pdp-cuboid");
      var cover = root.querySelector(".pdp-cover-frame");
      if (!cuboid || !cover) return;
      cuboid.hidden = true;
      cover.hidden = false;
    },
    restore: function (root) {
      root = root.querySelector(".pdp-gallery:not([hidden])") || root;
      var cuboid = root.querySelector(".pdp-cuboid");
      if (!cuboid) return;
      var cover = root.querySelector(".pdp-cover-frame");
      cuboid.hidden = false;
      if (cover) cover.hidden = true;
    }
  };
})();
